import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Textarea } from "../components/ui/Textarea";
import { Select } from "../components/ui/Select";
import { Card, CardBody } from "../components/ui/Card";
import { Sheet } from "../components/ui/Sheet";
import { api, ApiException } from "../lib/api";
import { ctLocalToUtc, fmtTimeCt, arriveByIso } from "../lib/dates";
import { cn } from "../lib/cn";

interface Provider {
  id: string;
  name: string;
  specialty: string | null;
  facility_name: string | null;
  address: string | null;
  phone: string | null;
}

interface Member {
  id: string;
  email: string;
  name: string | null;
  role: "admin" | "member";
}

interface MeResponse {
  user: { id: string } | null;
  circles: Array<{ id: string; role: "admin" | "member" }>;
}

const CATEGORIES = [
  ["office_visit", "Office visit"],
  ["follow_up", "Follow-up"],
  ["new_patient", "New patient"],
  ["procedure", "Procedure"],
  ["telehealth", "Telehealth"],
  ["lab", "Lab"],
  ["imaging", "Imaging"],
  ["other", "Other"],
] as const;

const LEAD_OPTIONS = [0, 15, 30, 45];

export function VisitNew() {
  const navigate = useNavigate();
  const [circleId, setCircleId] = useState<string | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [preCovered, setPreCovered] = useState(false);
  const [coveredMode, setCoveredMode] = useState<"member" | "name">("member");
  const [coveredUserId, setCoveredUserId] = useState("");
  const [coveredName, setCoveredName] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState<string | "">("");
  const [providerName, setProviderName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [facility, setFacility] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [category, setCategory] = useState<string>("office_visit");
  const [visitType, setVisitType] = useState<"in_person" | "telehealth">("in_person");
  const [meetingLink, setMeetingLink] = useState("");
  const [startsLocal, setStartsLocal] = useState("");      // datetime-local (CT wall-clock)
  const [duration, setDuration] = useState(60);
  const [leadMinutes, setLeadMinutes] = useState(15);
  const [customLead, setCustomLead] = useState("");
  const [rideRequired, setRideRequired] = useState(false);
  const [travelMin, setTravelMin] = useState<string>("");
  const [preInstructions, setPreInstructions] = useState("");
  const [notes, setNotes] = useState("");
  const [openInstructions, setOpenInstructions] = useState(false);
  const [providerSheetOpen, setProviderSheetOpen] = useState(false);
  const [newProviderName, setNewProviderName] = useState("");
  const [newProviderForm, setNewProviderForm] = useState({ specialty: "", facility_name: "", address: "", phone: "" });
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get<MeResponse>("/api/me");
        if (!me.user) return navigate("/signin", { replace: true });
        const circle = me.circles[0];
        if (!circle) return navigate("/app", { replace: true });
        setIsAdmin(circle.role === "admin");
        setCircleId(circle.id);
        const [providersRes, membersRes] = await Promise.all([
          api.get<{ providers: Provider[] }>(`/api/circles/${circle.id}/providers`),
          api.get<{ members: Member[] }>(`/api/circles/${circle.id}/members`),
        ]);
        setProviders(providersRes.providers);
        setMembers(membersRes.members);
      } catch (err) {
        if (err instanceof ApiException && err.status === 401) navigate("/signin", { replace: true });
      }
    })();
  }, [navigate]);

  function applyProvider(p: Provider | null) {
    setSelectedProviderId(p?.id ?? "");
    setProviderName(p?.name ?? "");
    setSpecialty(p?.specialty ?? "");
    setFacility(p?.facility_name ?? "");
    setAddress(p?.address ?? "");
    setPhone(p?.phone ?? "");
  }

  async function createProvider(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!circleId) return;
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.post<{ id: string }>(`/api/circles/${circleId}/providers`, {
        name: newProviderName,
        specialty: newProviderForm.specialty || null,
        facility_name: newProviderForm.facility_name || null,
        address: newProviderForm.address || null,
        phone: newProviderForm.phone || null,
      });
      const fresh: Provider = {
        id,
        name: newProviderName,
        specialty: newProviderForm.specialty || null,
        facility_name: newProviderForm.facility_name || null,
        address: newProviderForm.address || null,
        phone: newProviderForm.phone || null,
      };
      setProviders([fresh, ...providers]);
      applyProvider(fresh);
      setProviderSheetOpen(false);
      setNewProviderName("");
      setNewProviderForm({ specialty: "", facility_name: "", address: "", phone: "" });
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed to add provider.");
    } finally {
      setBusy(false);
    }
  }

  const effectiveLead = customLead.length > 0 ? Math.max(0, parseInt(customLead) || 0) : leadMinutes;
  const startsAtUtc = startsLocal ? ctLocalToUtc(startsLocal) : "";
  const endsAtUtc = startsAtUtc
    ? new Date(new Date(startsAtUtc).getTime() + duration * 60_000).toISOString()
    : "";

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!circleId) return;
    setBusy(true);
    setError(null);
    try {
      // Validate pre-covered selection.
      if (preCovered) {
        if (coveredMode === "member" && !coveredUserId) {
          throw new Error("Pick who is covering, or switch to enter a name.");
        }
        if (coveredMode === "name" && !coveredName.trim()) {
          throw new Error("Enter the name of the person covering this visit.");
        }
      }

      const payload = {
        provider_id: selectedProviderId || null,
        provider_name: providerName || null,
        provider_specialty: specialty || null,
        facility_name: facility || null,
        address: visitType === "telehealth" ? meetingLink || null : address || null,
        phone: phone || null,
        visit_category: category,
        visit_type: visitType,
        starts_at: startsAtUtc,
        ends_at: endsAtUtc || null,
        arrive_lead_minutes: effectiveLead,
        ride_required: rideRequired,
        estimated_travel_minutes: travelMin ? parseInt(travelMin, 10) : null,
        pre_visit_instructions: preInstructions || null,
        notes: notes || null,
        covered_by_user_id: preCovered && coveredMode === "member" ? coveredUserId : null,
        covered_by_name: preCovered && coveredMode === "name" ? coveredName.trim() : null,
      };
      const { id } = await api.post<{ id: string }>(`/api/circles/${circleId}/visits`, payload);
      navigate(`/app/visits/${id}`, { replace: true });
    } catch (err) {
      if (err instanceof Error && !(err instanceof ApiException)) {
        setError(err.message);
      } else {
        setError(err instanceof ApiException ? err.message : "Failed to create visit.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell activeTab="visits">
      <div
        className="mx-auto max-w-2xl px-5 py-6 sm:py-8"
        style={{ paddingBottom: "calc(11rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <h1 className="text-2xl font-semibold tracking-tight">
          {isAdmin ? "New visit" : "Propose a visit"}
        </h1>
        {!isAdmin && (
          <div className="mt-3 rounded-lg border border-primary/20 bg-primary-soft p-3 text-sm">
            <p className="text-text">
              <strong>Heads up:</strong> as a member, your proposal goes to admins for approval first.
              The family isn't notified until an admin approves it. You'll get an email when they decide.
            </p>
          </div>
        )}

        <form onSubmit={submit} className="mt-5 space-y-5" noValidate>
          {/* PROVIDER */}
          <Card>
            <CardBody>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">Provider</h2>
              {providers.length === 0 ? (
                <div className="mt-3 text-sm text-text-muted">
                  No providers yet.{" "}
                  <button type="button" onClick={() => setProviderSheetOpen(true)} className="text-primary underline">
                    Add one
                  </button>
                  .
                </div>
              ) : (
                <div className="mt-3">
                  <Label htmlFor="prov">Pick a provider</Label>
                  <Select
                    id="prov"
                    value={selectedProviderId}
                    onChange={(e) => {
                      const p = providers.find((x) => x.id === e.target.value);
                      applyProvider(p ?? null);
                    }}
                  >
                    <option value="">— Choose —</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.specialty ? ` · ${p.specialty}` : ""}
                      </option>
                    ))}
                  </Select>
                  <button
                    type="button"
                    onClick={() => setProviderSheetOpen(true)}
                    className="mt-2 text-sm text-primary underline"
                  >
                    + Add new provider
                  </button>
                </div>
              )}

              {providerName && (
                <div className="mt-4 space-y-3">
                  <div>
                    <Label htmlFor="pname">Doctor</Label>
                    <Input id="pname" value={providerName} onChange={(e) => setProviderName(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="spec">Specialty</Label>
                      <Input id="spec" value={specialty} onChange={(e) => setSpecialty(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="fac">Facility</Label>
                      <Input id="fac" value={facility} onChange={(e) => setFacility(e.target.value)} />
                    </div>
                  </div>
                  {visitType === "in_person" && (
                    <div>
                      <Label htmlFor="addr">Address</Label>
                      <Textarea id="addr" rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="ph">Phone</Label>
                    <Input id="ph" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* WHEN */}
          <Card>
            <CardBody>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">When</h2>
              <div className="mt-3 space-y-3">
                <div>
                  <Label htmlFor="starts">Start time (CT)</Label>
                  <Input
                    id="starts"
                    type="datetime-local"
                    required
                    value={startsLocal}
                    onChange={(e) => setStartsLocal(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Arrive how early?</Label>
                  <div className="flex gap-2 flex-wrap">
                    {LEAD_OPTIONS.map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => { setLeadMinutes(m); setCustomLead(""); }}
                        className={cn(
                          "px-3 py-2 rounded-md border text-sm font-medium",
                          customLead === "" && leadMinutes === m
                            ? "bg-primary text-primary-fg border-primary"
                            : "bg-surface text-text border-border hover:bg-surface-2",
                        )}
                      >
                        {m} min
                      </button>
                    ))}
                    <Input
                      type="number"
                      min={0}
                      max={240}
                      placeholder="Custom"
                      value={customLead}
                      onChange={(e) => setCustomLead(e.target.value)}
                      className="w-24"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="dur">Duration (minutes)</Label>
                  <Input
                    id="dur"
                    type="number"
                    min={5}
                    max={600}
                    value={duration}
                    onChange={(e) => setDuration(Math.max(5, parseInt(e.target.value, 10) || 60))}
                  />
                </div>
                {startsLocal && (
                  <div className="rounded-md bg-surface-2 p-3 text-sm">
                    <div className="font-medium">Arrive by {fmtTimeCt(arriveByIso(startsAtUtc, effectiveLead))}</div>
                    <div className="text-text-muted">
                      Starts {fmtTimeCt(startsAtUtc)} · ~{duration} min
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* LOGISTICS */}
          <Card>
            <CardBody>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">Logistics</h2>
              <div className="mt-3 space-y-3">
                <div>
                  <Label htmlFor="cat">Visit category</Label>
                  <Select id="cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                    {CATEGORIES.map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label>Visit type</Label>
                  <div className="flex gap-2">
                    {(["in_person", "telehealth"] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setVisitType(t)}
                        className={cn(
                          "flex-1 px-3 py-2 rounded-md border text-sm font-medium",
                          visitType === t
                            ? "bg-primary text-primary-fg border-primary"
                            : "bg-surface text-text border-border",
                        )}
                      >
                        {t === "in_person" ? "In person" : "Telehealth"}
                      </button>
                    ))}
                  </div>
                </div>
                {visitType === "telehealth" && (
                  <div>
                    <Label htmlFor="link">Meeting link</Label>
                    <Input id="link" type="url" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} />
                  </div>
                )}
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={rideRequired} onChange={(e) => setRideRequired(e.target.checked)} className="h-4 w-4" />
                  <span className="text-sm">Ride required</span>
                </label>
                {rideRequired && (
                  <div>
                    <Label htmlFor="travel">Estimated travel time (minutes)</Label>
                    <Input
                      id="travel"
                      type="number"
                      min={0}
                      max={600}
                      value={travelMin}
                      onChange={(e) => setTravelMin(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* ALREADY COVERED (admin-only — members can't pre-claim on someone's behalf) */}
          {isAdmin && (
          <Card>
            <CardBody>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">Already covered?</h2>
                  <p className="mt-1 text-sm text-text-muted">
                    Skip the "needs coverage" broadcast. The family will get a heads-up that says who's already taking it.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={preCovered}
                  onChange={(e) => setPreCovered(e.target.checked)}
                  className="h-5 w-5 accent-primary mt-0.5"
                  aria-label="Already covered"
                />
              </div>
              {preCovered && (
                <div className="mt-4 space-y-3">
                  <div className="flex gap-2">
                    {(["member", "name"] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setCoveredMode(mode)}
                        className={cn(
                          "flex-1 px-3 py-2 rounded-md border text-sm font-medium",
                          coveredMode === mode
                            ? "bg-primary text-primary-fg border-primary"
                            : "bg-surface text-text border-border",
                        )}
                      >
                        {mode === "member" ? "A family member" : "Someone else (by name)"}
                      </button>
                    ))}
                  </div>
                  {coveredMode === "member" ? (
                    <div>
                      <Label htmlFor="cov-user">Who's covering it?</Label>
                      <Select
                        id="cov-user"
                        value={coveredUserId}
                        onChange={(e) => setCoveredUserId(e.target.value)}
                      >
                        <option value="">— Pick someone —</option>
                        {members.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name ?? m.email}{m.role === "admin" ? " · admin" : ""}
                          </option>
                        ))}
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="cov-name">Name</Label>
                      <Input
                        id="cov-name"
                        value={coveredName}
                        onChange={(e) => setCoveredName(e.target.value)}
                        placeholder="e.g. Aunt Rose, or a neighbor"
                      />
                      <p className="mt-1 text-xs text-text-muted">
                        For when the person isn't in the family circle.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
          )}

          {/* INSTRUCTIONS */}
          <Card>
            <CardBody>
              <button
                type="button"
                className="w-full flex items-center justify-between text-left"
                onClick={() => setOpenInstructions(!openInstructions)}
              >
                <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">
                  Instructions & notes
                </h2>
                <span className="text-sm text-primary">{openInstructions ? "Hide" : "Add"}</span>
              </button>
              {openInstructions && (
                <div className="mt-3 space-y-3">
                  <div>
                    <Label htmlFor="instr">Pre-visit instructions</Label>
                    <Textarea
                      id="instr"
                      rows={3}
                      placeholder="Fasting requirements, what to bring, etc."
                      value={preInstructions}
                      onChange={(e) => setPreInstructions(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="ntz">Family notes</Label>
                    <Textarea
                      id="ntz"
                      rows={3}
                      placeholder="Anything for the acceptor — parking tips, mobility needs, mood"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {error && (
            <div role="alert" className="rounded-md border border-danger/30 bg-red-50 text-danger text-sm px-3 py-2">{error}</div>
          )}
        </form>

        {/* Sticky save bar */}
        <div className="fixed bottom-0 sm:left-60 inset-x-0 bg-surface border-t border-border p-3 sm:p-4 z-20">
          <div className="mx-auto max-w-2xl flex gap-3">
            <Button onClick={(e) => submit(e as unknown as FormEvent<HTMLFormElement>)} loading={busy} size="lg" className="flex-1">
              {isAdmin ? "Post visit" : "Send for approval"}
            </Button>
            <Link to="/app/visits">
              <Button variant="ghost" size="lg" type="button">Cancel</Button>
            </Link>
          </div>
        </div>

        <Sheet
          open={providerSheetOpen}
          onClose={() => setProviderSheetOpen(false)}
          title="Add provider"
          description="Saves to the circle's provider list so it autofills next time."
        >
          <form onSubmit={createProvider} className="space-y-4">
            <div>
              <Label htmlFor="np-name">Name</Label>
              <Input id="np-name" required value={newProviderName} onChange={(e) => setNewProviderName(e.target.value)} placeholder="Dr. N Vallala" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="np-spec">Specialty</Label>
                <Input id="np-spec" value={newProviderForm.specialty} onChange={(e) => setNewProviderForm({ ...newProviderForm, specialty: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="np-fac">Facility</Label>
                <Input id="np-fac" value={newProviderForm.facility_name} onChange={(e) => setNewProviderForm({ ...newProviderForm, facility_name: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="np-addr">Address</Label>
              <Textarea id="np-addr" rows={2} value={newProviderForm.address} onChange={(e) => setNewProviderForm({ ...newProviderForm, address: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="np-ph">Phone</Label>
              <Input id="np-ph" type="tel" value={newProviderForm.phone} onChange={(e) => setNewProviderForm({ ...newProviderForm, phone: e.target.value })} />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" loading={busy}>Save provider</Button>
              <Button type="button" variant="ghost" onClick={() => setProviderSheetOpen(false)}>Cancel</Button>
            </div>
          </form>
        </Sheet>
      </div>
    </AppShell>
  );
}
